import { aptGenerationApi } from '@/api/apt_generation.api';
import { modelsApi } from '@/api/models.api';
import { projectsApi } from '@/api/projects.api';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ConfirmDeleteDialog } from '@/components/dialogs/ConfirmDeleteDialog';
import { APTGenerationPanel } from '@/components/project-detail/APTGenerationPanel';
import { CollisionDetectionPanel } from '@/components/project-detail/CollisionDetectionPanel';
import { DetectionsPanel } from '@/components/project-detail/DetectionsPanel';
import { ModelColumn } from '@/components/project-detail/ModelColumn';
import { NCDocumentPanel } from '@/components/project-detail/NCDocumentPanel';
import { PocketsPanel } from '@/components/project-detail/PocketsPanel';
import { ProjectTitleEditor } from '@/components/project-detail/ProjectTitleEditor';
import { ProjectUploadingOverlay } from '@/components/project-detail/ProjectUploadingOverlay';
import { clearProjectTask, projectTaskStorageKey, useUploadQueueContext } from '@/context/UploadQueueContext';
import { useProjectDetail } from '@/hooks/useProjectDetail';
import { useProjects } from '@/hooks/useProjects';
import type { Model3DModel } from '@/types/project.types';
import { downloadBlob } from '@/utils/download';
import { ArrowLeft, Download } from 'lucide-react';
import { Button } from 'primereact/button';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export const ProjectDetail: FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const { project, isLoading, error, updateProject, refetch } = useProjectDetail(projectId!);

  // Project list hook still provides delete + refetch used by this page; the
  // model preview URLs/IDs now come from the consolidated overview payload.
  const { deleteProject, refetch: refetchProjects } = useProjects();

  const { getUploadForProject } = useUploadQueueContext();
  const activeUpload = projectId ? getUploadForProject(projectId) : undefined;

  const storedTaskFromLs =
    typeof window !== 'undefined' && projectId
      ? localStorage.getItem(projectTaskStorageKey(projectId))
      : null;

  const [serverUploadTaskId, setServerUploadTaskId] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const { task_id } = await modelsApi.getProjectModelUploadTask(projectId);
        if (!cancelled) {
          setServerUploadTaskId(task_id ?? null);
        }
      } catch {
        if (!cancelled) {
          setServerUploadTaskId(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const isUploadingFromQueue = Boolean(
    activeUpload && (activeUpload.status === 'uploading' || activeUpload.status === 'processing')
  );
  const isProcessingUpload =
    isUploadingFromQueue ||
    Boolean(!activeUpload && storedTaskFromLs) ||
    Boolean(serverUploadTaskId);

  const uploadOverlayTaskId = activeUpload?.taskId ?? storedTaskFromLs ?? serverUploadTaskId;
  const subscribeUploadOverlayToSse = !activeUpload && Boolean(storedTaskFromLs || serverUploadTaskId);

  const handleUploadOverlayComplete = useCallback(() => {
    if (projectId) {
      clearProjectTask(projectId);
    }
    setServerUploadTaskId(null);
    void refetch();
    void refetchProjects();
  }, [projectId, refetch, refetchProjects]);

  const prevProcessingUploadRef = useRef(false);
  useEffect(() => {
    if (projectId) {
      prevProcessingUploadRef.current = false;
    }
  }, [projectId]);

  useEffect(() => {
    if (prevProcessingUploadRef.current && !isProcessingUpload) {
      void refetch();
      void refetchProjects();
    }
    prevProcessingUploadRef.current = isProcessingUpload;
  }, [isProcessingUpload, refetch, refetchProjects]);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [_isDeleting, setIsDeleting] = useState(false);
  const [ncDocumentRefreshKey, setNcDocumentRefreshKey] = useState(0);
  const [targetRegenerating, setTargetRegenerating] = useState(false);
  const [blankRegenerating, setBlankRegenerating] = useState(false);
  const [aptGenerationReady, setAptGenerationReady] = useState(false);
  const [isDownloadingHFiles, setIsDownloadingHFiles] = useState(false);
  const [isDownloadingEmbedded, setIsDownloadingEmbedded] = useState(false);

  const handleDownloadEmbedded = async () => {
    if (!projectId) return;
    setIsDownloadingEmbedded(true);
    try {
      const blob = await projectsApi.downloadEmbeddedSTP(projectId);
      const embeddedFilename = `${(project?.title || projectId).replace(/ /g, '_')}-embedded.stp`;
      downloadBlob(blob, embeddedFilename);
      toast.success(t('models.embeddedDownloaded'));
    } catch (_e) {
      toast.error(t('models.embeddedDownloadFailed'));
    } finally {
      setIsDownloadingEmbedded(false);
    }
  };

  const handleDownloadProjectHFiles = async () => {
    if (!projectId) return;
    setIsDownloadingHFiles(true);
    try {
      const response = await aptGenerationApi.downloadProjectHFilesZip(projectId);
      let filename = `h_files_project_${projectId}.zip`;
      const contentDisposition = (response.headers as Record<string, string>)?.['content-disposition'];
      if (contentDisposition) {
        const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
        if (match?.[1]) filename = match[1];
      }
      const url = URL.createObjectURL(new Blob([response.data as BlobPart]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('projectDetail.downloadError') || 'Failed to download H files');
    } finally {
      setIsDownloadingHFiles(false);
    }
  };

  // Poll project detail while collision simulation is running
  const collisionState = project?.collision_simulation_state ?? null;

  useEffect(() => {
    if (collisionState !== 'running') return;
    const intervalMs = 8000;
    const id = setInterval(() => {
      refetch();
    }, intervalMs);
    return () => clearInterval(id);
  }, [collisionState, refetch]);

  // Model IDs + preview URLs come directly from the overview payload.
  const targetModelId = project?.target_model_id ?? null;
  const blankModelId = project?.blank_model_id ?? null;

  // Create mock Model3DModel objects for ModelColumn components
  const targetModel: Model3DModel | null = targetModelId
    ? ({
        id: targetModelId,
        project_id: projectId!,
        type: 'target',
        s3_url_stp: '',
        s3_url_png: project?.target_png_url || '',
        s3_url_gif: '',
        created_at: '',
        updated_at: '',
      } as Model3DModel)
    : null;

  const blankModel: Model3DModel | null = blankModelId
    ? ({
        id: blankModelId,
        project_id: projectId!,
        type: 'blank',
        s3_url_stp: '',
        s3_url_png: project?.blank_png_url || '',
        s3_url_gif: '',
        created_at: '',
        updated_at: '',
      } as Model3DModel)
    : null;

  const handleDeleteProject = async () => {
    setIsDeleting(true);
    try {
      await deleteProject(projectId!);
      navigate('/');
    } catch (_error) {
      // Error is handled by hook
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (isProcessingUpload) {
    return (
      <ProjectUploadingOverlay
        taskId={uploadOverlayTaskId}
        progressFromContext={activeUpload?.progress ?? 0}
        subscribeToTaskStream={subscribeUploadOverlayToSse}
        onComplete={handleUploadOverlayComplete}
        onStreamError={() => {
          if (projectId) {
            clearProjectTask(projectId);
          }
          setServerUploadTaskId(null);
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-[calc(80rem+4cm)] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorMessage message={error || 'Project not found'} />
        <Button
          label={t('projectDetail.backToProjects')}
          icon={<ArrowLeft size={16} />}
          onClick={() => navigate('/')}
          className="mt-4"
          outlined={true}
        />
      </div>
    );
  }

  if (project.apt_generation_error) {
    return (
      <div className="max-w-[calc(80rem+4cm)] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-gray-900"
            title={t('projectDetail.backToProjects')}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-semibold text-gray-900">{project.title}</h1>
        </div>
        <ErrorMessage message={project.apt_generation_error} />
      </div>
    );
  }

  return (
    <div className="max-w-[calc(80rem+4cm)] mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-gray-900"
                title={t('projectDetail.backToProjects')}
              >
                <ArrowLeft size={20} />
              </button>
              <ProjectTitleEditor title={project.title} onSave={updateProject} />
            </div>
            <div className="flex items-center gap-3">
              <Button
                label={t('projectDetail.downloadProjectHFiles')}
                icon={<Download size={16} className="mr-2" />}
                onClick={() => void handleDownloadProjectHFiles()}
                loading={isDownloadingHFiles}
                disabled={isDownloadingHFiles}
                outlined
                size="small"
                tooltip={t('projectDetail.downloadProjectHFilesTooltip')}
                tooltipOptions={{ position: 'bottom' }}
                aria-label={t('projectDetail.downloadProjectHFilesTooltip')}
              />
            </div>
          </div>
        </div>

        {/* Collision Detection */}
        <CollisionDetectionPanel project={project} />

        {/* NC Document: load only after all dependent sections are ready (APT Generation loaded including holes, no regeneration in progress) */}
        <NCDocumentPanel
          projectId={projectId!}
          projectTitle={project.title}
          refreshKey={ncDocumentRefreshKey}
          canLoad={aptGenerationReady && !targetRegenerating && !blankRegenerating}
          blurred={!aptGenerationReady || targetRegenerating || blankRegenerating}
        />

        {/* Model section header: title + embedded download */}
        <div className="bg-white p-6 rounded-lg shadow flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{t('models.modelSection')}</h2>
          {project.s3_url_stp_embedded && (
            <Button
              label={t('models.downloadEmbedded')}
              icon={<Download size={16} className="mr-2" />}
              onClick={handleDownloadEmbedded}
              outlined={true}
              loading={isDownloadingEmbedded}
              disabled={isDownloadingEmbedded}
            />
          )}
        </div>

        {/* 2-column layout for target + blank models */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ModelColumn
            title={t('models.targetModel')}
            modelType="target"
            projectId={projectId!}
            model={targetModel}
            onUpload={async () => {
              // Upload is handled in ModelColumn, refetch both project and projects list
              await refetch();
              await refetchProjects();
            }}
          />

          <ModelColumn
            title={t('models.blankModel')}
            modelType="blank"
            projectId={projectId!}
            model={blankModel}
            onUpload={async () => {
              // Upload is handled in ModelColumn, refetch both project and projects list
              await refetch();
              await refetchProjects();
            }}
          />
        </div>

        {/* Detections - 2 columns for target and blank models */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DetectionsPanel
            projectId={projectId!}
            modelId={targetModelId}
            title={t('models.targetModel')}
            modelType="target"
            onRegenerationComplete={() => setNcDocumentRefreshKey((k) => k + 1)}
            onRegeneratingChange={setTargetRegenerating}
          />
          <DetectionsPanel
            projectId={projectId!}
            modelId={blankModelId}
            title={t('models.blankModel')}
            modelType="blank"
            onRegenerationComplete={() => setNcDocumentRefreshKey((k) => k + 1)}
            onRegeneratingChange={setBlankRegenerating}
          />
        </div>

        {/* APT Generation */}
        <APTGenerationPanel
          projectId={projectId!}
          targetModelId={targetModelId}
          onAptGenerationReady={setAptGenerationReady}
        />

        <PocketsPanel projectId={projectId!} targetModelId={targetModelId} />

        {/* Actions */}
        <div className="flex justify-between">
          <Button
            label={t('projectDetail.backToProjects')}
            icon={<ArrowLeft size={16} />}
            onClick={() => navigate('/')}
            outlined={true}
          />
          <div className="flex gap-2">
            <Button
              label={t('projectDetail.deleteProject')}
              onClick={() => setShowDeleteDialog(true)}
              severity="danger"
            />
          </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDeleteDialog
        visible={showDeleteDialog}
        title={t('projectDetail.deleteProjectTitle')}
        message="Are you sure you want to delete this project? This action cannot be undone."
        onConfirm={handleDeleteProject}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};
